import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Bell, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationPopup = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPendingNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchPendingNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchPendingNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/notifications/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pending = response.data.notifications || [];
      setNotifications(pending);
      
      // Show first unacknowledged notification
      if (pending.length > 0 && !currentNotification) {
        setCurrentNotification(pending[0]);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleAcknowledge = async () => {
    if (!currentNotification) return;
    
    setAcknowledging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/notifications/${currentNotification.id}/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list and show next
      const remaining = notifications.filter(n => n.id !== currentNotification.id);
      setNotifications(remaining);
      setCurrentNotification(remaining.length > 0 ? remaining[0] : null);
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'urgent':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-600',
          icon: AlertTriangle,
          iconColor: 'text-white',
          badge: 'bg-red-100 text-red-700'
        };
      case 'important':
        return {
          bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          icon: Bell,
          iconColor: 'text-white',
          badge: 'bg-amber-100 text-amber-700'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
          icon: Info,
          iconColor: 'text-white',
          badge: 'bg-blue-100 text-blue-700'
        };
    }
  };

  if (!currentNotification) return null;

  const styles = getPriorityStyles(currentNotification.priority);
  const IconComponent = styles.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="w-full max-w-md transform animate-bounce-in">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className={`${styles.bg} p-6 pb-12 relative`}>
            {/* Badge */}
            {notifications.length > 1 && (
              <div className="absolute top-4 right-4 px-3 py-1 bg-white/20 rounded-full text-white text-sm font-medium">
                1 of {notifications.length}
              </div>
            )}
            
            {/* Icon */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                <IconComponent className={`w-8 h-8 ${currentNotification.priority === 'urgent' ? 'text-red-500' : currentNotification.priority === 'important' ? 'text-amber-500' : 'text-blue-500'}`} />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-12 pb-6 px-6 text-center">
            <span className={`inline-block px-3 py-1 ${styles.badge} text-xs font-semibold rounded-full uppercase tracking-wider mb-3`}>
              {currentNotification.priority === 'urgent' ? 'Urgent' : currentNotification.priority === 'important' ? 'Important' : 'Notification'}
            </span>
            
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              {currentNotification.title}
            </h2>
            
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap mb-6">
              {currentNotification.message}
            </p>
            
            <div className="text-xs text-slate-400 mb-4">
              From Admin • {new Date(currentNotification.created_at).toLocaleString()}
            </div>
            
            {/* Acknowledge Button */}
            <Button
              onClick={handleAcknowledge}
              disabled={acknowledging}
              className={`w-full py-3 ${styles.bg} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all`}
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              {acknowledging ? 'Acknowledging...' : 'I Acknowledge'}
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.8); }
          50% { transform: scale(1.02); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default NotificationPopup;
